import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

const categories = [
  { labelKey: 'dropdown.shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { labelKey: 'dropdown.hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { labelKey: 'dropdown.tshirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { labelKey: 'dropdown.jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { labelKey: 'dropdown.pants', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { labelKey: 'dropdown.headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { labelKey: 'dropdown.sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { labelKey: 'dropdown.underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { labelKey: 'dropdown.jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { labelKey: 'dropdown.accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { labelKey: 'dropdown.bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { labelKey: 'dropdown.belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { labelKey: 'dropdown.perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { labelKey: 'dropdown.other', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

const languages = [
  { code: 'pt', labelKey: 'lang.pt' },
  { code: 'de', labelKey: 'lang.de' },
  { code: 'en', labelKey: 'lang.en' },
];

export default function Header() {
  const { t, i18n } = useTranslation(['common']);
  const navigate = useNavigate();
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const changeLanguage = (code: string) => {
    i18n.changeLanguage(code);
    setLangOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b-2 border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo / Home */}
          <Link
            to="/"
            className="flex items-center gap-2 font-bold text-lg md:text-xl tracking-tight text-gray-900 cursor-pointer"
          >
            <span className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center text-white text-sm font-extrabold">
              C
            </span>
            <span className="hidden sm:inline">{t('common:site.name')}</span>
          </Link>

          {/* Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {t('common:nav.home')}
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors cursor-pointer"
              >
                {t('common:nav.categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 py-2 overflow-hidden">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t('common:dropdown.allCategories')}
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.labelKey}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                        onClick={() => setCatOpen(false)}
                      >
                        {t(`common:${cat.labelKey}`)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/article/cssbuy-beginner-guide"
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {t('common:nav.shoppingGuide')}
            </Link>

            <Link
              to="/article/cssbuy-spreadsheet-guide"
              className="text-sm font-semibold text-gray-900 hover:text-emerald-700 transition-colors cursor-pointer"
            >
              {t('common:nav.guides')}
            </Link>
          </nav>

          {/* Right */}
          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 text-sm font-semibold text-gray-700 hover:bg-gray-200 transition-colors cursor-pointer"
              >
                <i className="ri-global-line" />
                <span className="uppercase text-xs">{i18n.language}</span>
                <i className={`ri-arrow-down-s-line text-xs transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-200 py-2 overflow-hidden">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors cursor-pointer ${
                        i18n.language === lang.code
                          ? 'bg-emerald-50 text-emerald-700 font-medium'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {t(`common:${lang.labelKey}`)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* CTA */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap"
            >
              {t('common:cta.buyNow')}
            </a>

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-gray-900 hover:text-emerald-700 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-4 max-h-[calc(100vh-4rem)] overflow-y-auto overscroll-contain">
            <nav className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50"
              >
                <i className="ri-home-4-line text-lg text-emerald-600" />
                {t('common:nav.home')}
              </Link>
              <Link
                to="/article/cssbuy-beginner-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50"
              >
                <i className="ri-shopping-bag-3-line text-lg text-emerald-600" />
                {t('common:nav.shoppingGuide')}
              </Link>
              <Link
                to="/article/cssbuy-spreadsheet-guide"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm font-semibold text-gray-900 hover:bg-gray-50"
              >
                <i className="ri-book-open-line text-lg text-emerald-600" />
                {t('common:nav.guides')}
              </Link>

              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="px-3 pb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  {t('common:dropdown.allCategories')}
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {categories.map((cat) => (
                    <a
                      key={cat.labelKey}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="px-3 py-2.5 rounded-lg text-sm text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      {t(`common:${cat.labelKey}`)}
                    </a>
                  ))}
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setMobileOpen(false);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer ${
                      i18n.language === lang.code
                        ? 'bg-emerald-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t(`common:${lang.labelKey}`)}
                  </button>
                ))}
              </div>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}